<template>
  <div class="hamburger-menu">
    <button
      class="hamburger-button"
      :aria-expanded="isOpen"
      aria-label="Menu"
      @click="toggleMenu"
    >
      ☰
    </button>

    <transition name="slide">
      <nav
        v-if="isOpen"
        class="menu-panel"
      >
        <div class="menu-header">
          <h2>Menu</h2>
          <button
            class="close-button"
            aria-label="Close menu"
            @click="closeMenu"
          >
            ✕
          </button>
        </div>

        <ul class="menu-items">
          <li>
            <router-link
              to="/home"
              class="menu-item"
              @click="closeMenu"
            >
              <span class="menu-icon icon-xs">🏠</span>
              <span class="menu-label">Home</span>
            </router-link>
          </li>
          <li>
            <router-link
              to="/player"
              class="menu-item"
              @click="closeMenu"
            >
              <span class="menu-icon icon-xs">🎵</span>
              <span class="menu-label">Player</span>
            </router-link>
          </li>
          <li v-if="auth.isAuthenticated.value">
            <router-link
              to="/user-status"
              class="menu-item"
              @click="closeMenu"
            >
              <span class="menu-icon icon-xs">👤</span>
              <span class="menu-label">Status</span>
            </router-link>
          </li>
          <li>
            <router-link
              to="/guide"
              class="menu-item"
              @click="closeMenu"
            >
              <span class="menu-icon icon-xs">📖</span>
              <span class="menu-label">User Guide</span>
            </router-link>
          </li>
          <li>
            <router-link
              to="/pricing"
              class="menu-item"
              @click="closeMenu"
            >
              <span class="menu-icon icon-xs">💎</span>
              <span class="menu-label">Subscriptions</span>
            </router-link>
          </li>
          <li>
            <router-link
              to="/container-setup"
              class="menu-item"
              @click="closeMenu"
            >
              <span class="menu-icon icon-xs">🐳</span>
              <span class="menu-label">Container Setup</span>
            </router-link>
          </li>
          <li>
            <router-link
              to="/support"
              class="menu-item"
              @click="closeMenu"
            >
              <span class="menu-icon icon-xs">💬</span>
              <span class="menu-label">Support</span>
            </router-link>
          </li>
          <li>
            <router-link
              to="/acknowledgements"
              class="menu-item"
              @click="closeMenu"
            >
              <span class="menu-icon icon-xs">🙏</span>
              <span class="menu-label">Acknowledgements</span>
            </router-link>
          </li>
          <li v-if="auth.isAdmin.value">
            <router-link
              to="/admin"
              class="menu-item admin-menu-item"
              @click="closeMenu"
            >
              <span class="menu-icon icon-xs">👤</span>
              <span class="menu-label">Admin</span>
            </router-link>
          </li>
        </ul>
      </nav>
    </transition>

    <!-- Overlay to close menu when clicking outside -->
    <div
      v-if="isOpen"
      class="menu-overlay"
      @click="closeMenu"
    />
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useAuth } from '../composables/useAuth';

const isOpen = ref(false);
const auth = useAuth();

const toggleMenu = (): void => {
  isOpen.value = !isOpen.value;
};

const closeMenu = (): void => {
  isOpen.value = false;
};
</script>

<style scoped>
.hamburger-menu {
  position: relative;
}

.hamburger-button {
  background: none;
  border: none;
  font-size: var(--font-size-xl);
  cursor: pointer;
  padding: var(--spacing-sm) var(--spacing-md);
  color: var(--color-beige);
  transition: var(--transition-normal);
  line-height: var(--line-height-none);
}

.hamburger-button:hover {
  color: var(--color-text-inverse);
}

.hamburger-button:focus {
  outline: 2px solid var(--brand-accent);
  outline-offset: 2px;
  border-radius: var(--radius-sm);
}

.menu-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--color-overlay);
  z-index: var(--z-overlay);
}

.menu-panel {
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  width: 300px;
  background: var(--color-card-bg);
  box-shadow: var(--shadow-lg);
  z-index: var(--z-dialog);
  display: flex;
  flex-direction: column;
}

.menu-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--spacing-xl);
  border-bottom: 1px solid var(--color-border);
}

.menu-header h2 {
  margin: 0;
  font-size: var(--font-size-xl);
  color: var(--color-text-primary);
}

/* Close button now uses global .close-button utility class */

.menu-items {
  list-style: none;
  padding: 0;
  margin: 0;
  flex: 1;
  overflow-y: auto;
}

.menu-items li {
  border-bottom: 1px solid var(--color-divider);
}

.menu-item {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-lg) var(--spacing-xl);
  color: var(--color-text-primary);
  text-decoration: none;
  transition: var(--transition-normal);
}

.menu-item:hover {
  background-color: var(--color-hover);
}

.menu-item.router-link-active {
  background-color: var(--brand-accent);
  color: var(--color-dark-text-primary);
}

.menu-icon {
  width: 24px;
  text-align: center;
}

.menu-label {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
}

.admin-menu-item {
  border-top: 2px solid var(--color-border);
}

/* Slide transition */
.slide-enter-active,
.slide-leave-active {
  transition: var(--transition-slow);
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(-100%);
}

.slide-enter-to,
.slide-leave-from {
  transform: translateX(0);
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .menu-panel {
    width: 80%;
    max-width: 300px;
  }
}
</style>
